/**
 * PaymentRibbon — FieldPulse-style diagonal corner ribbon shown on top
 * of a project card / project detail header. Renders one of:
 *   - "PAID"             when at least one invoice and all are fully paid.
 *   - "PARTIAL PAYMENT"  when there is at least one payment but balance > 0.
 *   - "INVOICED"         when there is at least one invoice but no payment yet.
 *   - null               otherwise (no invoice yet) — no ribbon at all.
 *
 * The state is **derived** from the invoices array, NOT from the project
 * status. It is presentation only — does not change the auto-pipeline.
 */

export function computePaymentRibbon(invoices = []) {
  const active = invoices.filter(i => (i?.status || '').toLowerCase() !== 'cancelled');
  if (active.length === 0) return null;

  let totalAmount = 0;
  let totalPaid = 0;
  for (const inv of active) {
    totalAmount += Number(inv.total || 0);
    totalPaid += Number(inv.amount_paid || 0);
  }
  if (totalAmount <= 0) return null;

  if (totalPaid <= 0) return 'INVOICED';
  if (totalPaid >= totalAmount - 0.01) return 'PAID';
  return 'PARTIAL PAYMENT';
}

const RIBBON_COLORS = {
  PAID: 'bg-emerald-600',
  'PARTIAL PAYMENT': 'bg-amber-500',
  INVOICED: 'bg-indigo-500',
};

/**
 * Card-corner diagonal ribbon. Position absolute — parent must be `relative`
 * and have `overflow-hidden` for the clip to look right.
 */
export default function PaymentRibbon({ label, size = 'sm' }) {
  if (!label) return null;
  const bg = RIBBON_COLORS[label] || 'bg-slate-500';
  const textSize = size === 'lg' ? 'text-[11px]' : 'text-[9px]';
  const widthClass = size === 'lg' ? 'w-44' : 'w-36';
  const offsetClass = size === 'lg' ? '-right-12 top-5' : '-right-10 top-3';

  return (
    <div
      data-testid={`payment-ribbon-${label.replace(/\s+/g, '-').toLowerCase()}`}
      className={`absolute ${offsetClass} ${widthClass} ${bg} text-white ${textSize} font-bold tracking-wider text-center py-1 rotate-45 shadow-md pointer-events-none select-none uppercase`}
    >
      {label}
    </div>
  );
}
